import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { X, Plus, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { type AgentBuilderState } from '../AgentBuilder'

interface PersonalityStepProps {
  state: AgentBuilderState
  updateState: (updates: Partial<AgentBuilderState>) => void
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Formal and business-appropriate' },
  { value: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
  { value: 'casual', label: 'Casual', description: 'Relaxed and conversational' },
  { value: 'academic', label: 'Academic', description: 'Scholarly and precise' },
  { value: 'creative', label: 'Creative', description: 'Imaginative and expressive' },
]

const STYLE_OPTIONS = [
  { value: 'concise', label: 'Concise', description: 'Brief and to the point' },
  { value: 'detailed', label: 'Detailed', description: 'Thorough and comprehensive' },
  { value: 'balanced', label: 'Balanced', description: 'Mix of concise and detailed' },
  { value: 'conversational', label: 'Conversational', description: 'Natural dialogue style' },
]

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'nl', label: 'Dutch' },
  { value: 'ja', label: 'Japanese' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ko', label: 'Korean' },
]

const EXPERTISE_SUGGESTIONS = [
  'Business Writing',
  'Technical Documentation',
  'Marketing',
  'Legal',
  'Healthcare',
  'Finance',
  'Education',
  'Customer Service',
  'Sales',
  'HR',
]

export function PersonalityStep({ state, updateState }: PersonalityStepProps) {
  const [expertiseInput, setExpertiseInput] = useState('')

  const updatePersonality = (updates: Partial<typeof state.personality>) => {
    updateState({
      personality: { ...state.personality, ...updates },
    })
  }

  const addExpertise = (expertise: string) => {
    if (expertise && !state.personality.expertise.includes(expertise)) {
      updatePersonality({
        expertise: [...state.personality.expertise, expertise],
      })
    }
    setExpertiseInput('')
  }

  const removeExpertise = (expertise: string) => {
    updatePersonality({
      expertise: state.personality.expertise.filter((e) => e !== expertise),
    })
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Personality Configuration
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Define how your agent communicates and interacts with users.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tone */}
        <div className="space-y-3">
          <Label className="text-gray-700 dark:text-gray-300">Communication Tone</Label>
          <Select
            value={state.personality.tone}
            onValueChange={(value) => updatePersonality({ tone: value })}
          >
            <SelectTrigger className="rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TONE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div>
                    <p className="font-medium">{option.label}</p>
                    <p className="text-xs text-gray-500">{option.description}</p>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Style */}
        <div className="space-y-3">
          <Label className="text-gray-700 dark:text-gray-300">Writing Style</Label>
          <Select
            value={state.personality.style}
            onValueChange={(value) => updatePersonality({ style: value })}
          >
            <SelectTrigger className="rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STYLE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div>
                    <p className="font-medium">{option.label}</p>
                    <p className="text-xs text-gray-500">{option.description}</p>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Language */}
        <div className="space-y-3">
          <Label className="text-gray-700 dark:text-gray-300">Primary Language</Label>
          <Select
            value={state.personality.language}
            onValueChange={(value) => updatePersonality({ language: value })}
          >
            <SelectTrigger className="rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-6">
        {/* Formality Level */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-gray-700 dark:text-gray-300">Formality Level</Label>
            <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
              {state.personality.formalityLevel ?? 5}/10
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 w-16">Casual</span>
            <Slider
              value={[state.personality.formalityLevel ?? 5]}
              onValueChange={([value]) => updatePersonality({ formalityLevel: value })}
              min={1}
              max={10}
              step={1}
              className="flex-1"
            />
            <span className="text-sm text-gray-500 w-16 text-right">Formal</span>
          </div>
        </div>

        {/* Creativity Level */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-gray-700 dark:text-gray-300">Creativity Level</Label>
            <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
              {state.personality.creativityLevel ?? 5}/10
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 w-16">Factual</span>
            <Slider
              value={[state.personality.creativityLevel ?? 5]}
              onValueChange={([value]) => updatePersonality({ creativityLevel: value })}
              min={1}
              max={10}
              step={1}
              className="flex-1"
            />
            <span className="text-sm text-gray-500 w-16 text-right">Creative</span>
          </div>
        </div>

        {/* Verbosity Level */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-gray-700 dark:text-gray-300">Verbosity Level</Label>
            <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
              {state.personality.verbosityLevel ?? 5}/10
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 w-16">Brief</span>
            <Slider
              value={[state.personality.verbosityLevel ?? 5]}
              onValueChange={([value]) => updatePersonality({ verbosityLevel: value })}
              min={1}
              max={10}
              step={1}
              className="flex-1"
            />
            <span className="text-sm text-gray-500 w-16 text-right">Verbose</span>
          </div>
        </div>
      </div>

      {/* Expertise Areas */}
      <div className="space-y-4">
        <Label className="text-gray-700 dark:text-gray-300">Expertise Areas</Label>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Add specific domains or industries the agent should be knowledgeable about.
        </p>

        <div className="flex gap-2">
          <Input
            value={expertiseInput}
            onChange={(e) => setExpertiseInput(e.target.value)}
            placeholder="Add expertise area..."
            className="rounded-xl"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addExpertise(expertiseInput)
              }
            }}
          />
          <button
            onClick={() => addExpertise(expertiseInput)}
            disabled={!expertiseInput}
            className="p-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>

        {/* Selected Expertise */}
        {state.personality.expertise.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {state.personality.expertise.map((exp) => (
              <Badge
                key={exp}
                className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-3 py-1"
              >
                {exp}
                <button
                  onClick={() => removeExpertise(exp)}
                  className="ml-2 hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Suggestions */}
        <div className="space-y-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">Suggestions:</p>
          <div className="flex flex-wrap gap-2">
            {EXPERTISE_SUGGESTIONS.filter(
              (s) => !state.personality.expertise.includes(s)
            ).map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => addExpertise(suggestion)}
                className="px-3 py-1 text-sm rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
              >
                + {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="p-6 rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-purple-500" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Personality Preview</p>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm italic">
          "This agent communicates in a{' '}
          <strong>{state.personality.tone}</strong> tone with a{' '}
          <strong>{state.personality.style}</strong> writing style.
          {(state.personality.formalityLevel ?? 5) >= 7
            ? ' It maintains a formal and professional demeanor.'
            : (state.personality.formalityLevel ?? 5) <= 3
            ? ' It uses a casual and relaxed approach.'
            : ' It balances formality with approachability.'}
          {(state.personality.creativityLevel ?? 5) >= 7
            ? ' Responses are creative and imaginative.'
            : (state.personality.creativityLevel ?? 5) <= 3
            ? ' Responses focus on facts and accuracy.'
            : ' It balances creativity with practicality.'}
          {state.personality.expertise.length > 0 &&
            ` Specialized in: ${state.personality.expertise.join(', ')}.`}
          "
        </p>
      </div>
    </div>
  )
}
